import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Plus, Minus, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useCart } from '../contexts/CartContext';
import { t, getProductName } from '../lib/translations';

export const ProductCard = ({ product }) => {
  const { addToCart, cart, updateQuantity } = useCart();
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);

  const cartItem = cart.items.find(item => item.product_id === product.id);
  const quantity = cartItem?.quantity || 0;

  const formatPrice = (price) => `₹${price.toFixed(0)}`;
  const hasDiscount = product.discount_price && product.discount_price < product.price;
  const discountPercent = hasDiscount 
    ? Math.round(((product.price - product.discount_price) / product.price) * 100) 
    : 0;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAdding(true);
    const success = await addToCart(product.id, 1);
    setAdding(false);
    if (success) {
      setAdded(true);
      setTimeout(() => setAdded(false), 1500);
    }
  };

  const handleQuantityChange = async (e, newQty) => {
    e.preventDefault();
    e.stopPropagation();
    await updateQuantity(product.id, newQty);
  };

  return (
    <Link to={`/product/${product.id}`} className="block" data-testid={`product-card-${product.id}`}>
      <div className="product-card bg-white border border-slate-100 rounded-xl overflow-hidden group">
        {/* Image */}
        <div className="relative aspect-square overflow-hidden bg-slate-100">
          <img
            src={product.image}
            alt={getProductName(product)}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
          
          {/* Discount Badge */}
          {hasDiscount && (
            <Badge className="absolute top-2 left-2 bg-orange-500 text-white">
              -{discountPercent}%
            </Badge>
          )}
          
          {/* Out of Stock Overlay */}
          {product.stock === 0 && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="text-white font-medium px-3 py-1 bg-red-500 rounded-full text-sm">
                {t('outOfStock')}
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-3 sm:p-4">
          {/* Name */}
          <h3 className="font-medium text-slate-900 truncate text-sm sm:text-base">
            {getProductName(product)}
          </h3>
          
          {/* Unit */}
          <p className="text-xs text-slate-500 mt-0.5">{product.unit}</p>

          {/* Price & Add to Cart */}
          <div className="flex items-center justify-between mt-2 gap-2">
            <div className="flex items-center gap-2">
              <span className="font-bold text-green-600 text-sm sm:text-base">
                {formatPrice(hasDiscount ? product.discount_price : product.price)}
              </span>
              {hasDiscount && (
                <span className="text-xs text-slate-400 line-through">
                  {formatPrice(product.price)}
                </span>
              )}
            </div>

            {/* Add to Cart Button */}
            {product.stock > 0 && (
              quantity > 0 ? (
                <div className="flex items-center gap-1" onClick={(e) => e.preventDefault()}>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7 sm:h-8 sm:w-8"
                    onClick={(e) => handleQuantityChange(e, quantity - 1)}
                    data-testid={`decrease-${product.id}`}
                  >
                    <Minus className="w-3 h-3" />
                  </Button>
                  <span className="w-6 text-center font-medium text-sm">{quantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7 sm:h-8 sm:w-8"
                    onClick={(e) => handleQuantityChange(e, quantity + 1)}
                    data-testid={`increase-${product.id}`}
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  className={`h-7 sm:h-8 px-2 sm:px-3 text-xs sm:text-sm btn-press ${
                    added ? 'bg-green-500' : 'bg-green-600 hover:bg-green-700'
                  }`}
                  onClick={handleAddToCart}
                  disabled={adding}
                  data-testid={`add-to-cart-${product.id}`}
                >
                  {added ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <>
                      <ShoppingCart className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      <span className="hidden sm:inline">{t('addToCart')}</span>
                      <span className="sm:hidden">Add</span>
                    </>
                  )}
                </Button>
              )
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
